import moment from 'moment-timezone';
import { getInfo } from '../components/kpis/KPIViewPage';
import { NHN_PWN_disabled, roles, userAllowed } from './utils';
import { SOLUTION_TYPE } from './const';

const isCoreNetworkTabEnabled = (solutionType, userRoles) => {
    if (solutionType === SOLUTION_TYPE.NHN_PWN) {
        return (
            userAllowed(userRoles, [roles.MANAGE_NHN_PWN_FEATURES]) &&
            userAllowed(userRoles, [roles.MANAGE_4G_ATTACHED_DEVICES, roles.MANAGE_5GSA_REGISTERED_DEVICES], true)
        );
    }
    return userAllowed(
        userRoles,
        [
            roles.MANAGE_4G_ATTACHED_DEVICES,
            roles.MANAGE_5GSA_REGISTERED_DEVICES,
            roles.MANAGE_4G_ATTACH_SUCCESS_RATE,
            roles.MANAGE_5GSA_REGISTRATION_SUCCESS_RATE,
            roles.MANAGE_4G_CORE_DL_THROUGHPUT,
            roles.MANAGE_5G_CORE_DL_THROUGHPUT,
            roles.MANAGE_4G_CORE_UL_THROUGHPUT,
            roles.MANAGE_5G_CORE_UL_THROUGHPUT
        ],
        true
    );
};

export const performanceLicensing = (page, userRoles, solutionType = '') => {
    switch (page) {
        case 'overView':
            return userAllowed(
                userRoles,
                [
                    roles.MANAGE_4G_ATTACHED_DEVICES,
                    roles.MANAGE_5GSA_REGISTERED_DEVICES,
                    roles.MANAGE_4G_ATTACH_SUCCESS_RATE,
                    roles.MANAGE_5GSA_REGISTRATION_SUCCESS_RATE,
                    roles.MANAGE_4G_RAN_DL_THROUGHPUT,
                    roles.MANAGE_4G_RAN_UL_THROUGHPUT,
                    roles.MANAGE_5G_RAN_DL_THROUGHPUT,
                    roles.MANAGE_5G_RAN_UL_THROUGHPUT,
                    roles.MANAGE_4G5G_RAN_UL_THROUGHPUT,
                    roles.MANAGE_4G5G_RAN_DL_THROUGHPUT,
                    roles.MANAGE_4G_CORE_DL_THROUGHPUT,
                    roles.MANAGE_4G_CORE_UL_THROUGHPUT,
                    roles.KPI_4GRAN_DATA_ACCESSIBILITY,
                    roles.KPI_4GRAN_DATA_MOBILITY,
                    roles.KPI_4GRAN_DATA_RETAINABILITY,
                    roles.VIEW_KPI_VoLTE_ACCESSIBILITY,
                    roles.VIEW_KPI_VoLTE_RETAINABILITY,
                    roles.VIEW_KPI_VoLTE_MOBILITY,
                    roles.VIEW_KPI_5G_NSA_ACCESSIBILITY,
                    roles.VIEW_KPI_5G_NSA_RETAINABILITY,
                    roles.VIEW_KPI_5G_NSA_MOBILITY
                ],
                true
            );
        case 'coreNetwork':
            return isCoreNetworkTabEnabled(solutionType, userRoles);

        case 'radioNetwork':
            return userAllowed(
                userRoles,
                [
                    roles.MANAGE_4G_RAN_DL_THROUGHPUT,
                    roles.MANAGE_5G_RAN_DL_THROUGHPUT,
                    roles.MANAGE_4G_RAN_UL_THROUGHPUT,
                    roles.MANAGE_5G_RAN_UL_THROUGHPUT,
                    roles.MANAGE_4G5G_RAN_UL_THROUGHPUT,
                    roles.MANAGE_4G5G_RAN_UL_THROUGHPUT,
                    roles.KPI_4GRAN_DATA_ACCESSIBILITY,
                    roles.KPI_4GRAN_DATA_MOBILITY,
                    roles.KPI_4GRAN_DATA_RETAINABILITY,
                    roles.VIEW_KPI_VoLTE_ACCESSIBILITY,
                    roles.VIEW_KPI_VoLTE_RETAINABILITY,
                    roles.VIEW_KPI_VoLTE_MOBILITY,
                    roles.VIEW_KPI_5G_NSA_ACCESSIBILITY,
                    roles.VIEW_KPI_5G_NSA_RETAINABILITY,
                    roles.VIEW_KPI_5G_NSA_MOBILITY
                ],
                true
            );

        case 'usage':
            return !NHN_PWN_disabled(solutionType, userRoles) && userAllowed(userRoles, [roles.MANAGE_TOP_DEVICES_EP5G]);
    }
};

export const getCombinedGraphValue = (downlinkValue, uplinkValue) => {
    let derived;
    if (typeof downlinkValue === 'undefined' && typeof uplinkValue === 'undefined') {
        derived = undefined;
    } else if (downlinkValue === -1 && uplinkValue === -1) {
        derived = -1;
    } else if (downlinkValue === -1 || typeof downlinkValue === 'undefined') {
        derived = uplinkValue;
    } else if (uplinkValue === -1 || typeof uplinkValue === 'undefined') {
        derived = downlinkValue;
    } else {
        derived = downlinkValue + uplinkValue;
    }
    return !!derived ? derived.toFixed(2) : derived;
};

export const getNodeSuffix = (element) => {
    return !!element ? `_${element}` : '';
};

export const getPartNameNodeOption = (node) => {
    const commonName = !!node?.commonName ? `${node?.commonName}_` : '';
    const text = commonName.concat(node.nodeName.concat(getNodeSuffix(node.partName)));
    const option = node.nodeName.concat(getNodeSuffix(node.partName));
    return {
        key: option,
        text: text,
        value: option,
        commonName: node?.commonName,
        nodeType: node.nodeType,
        unitId: node.unitId,
        vendorName: node.vendorName,
        cellType: node?.cellType,
        networkElementType: node?.networkElementType,
        radioAccessTechnology: node.radioAccessTechnology
    };
};

const euTranCellTypeTDD = (unitIdVal, cellType, sharedCells, nodeCount) => {
    let retValTemp = unitIdVal?.split('-');
    if ((nodeCount > 1 || cellType === 'FDD' || sharedCells != null) && retValTemp.length >= 2) {
        const retVal = retValTemp[0].concat('-').concat(retValTemp[1]);
        return retVal;
    }
    return unitIdVal;
};

export const getUnitIdNodeOption = (node, nodeNameCounts, cellRadioMap, addUnitFlagValueInOptionValue, isNDAC, isOutdoor5GBB) => {
    const cellId = node.cellId,
        unitIdVal = node.unitId,
        cellType = node?.cellType,
        networkElementType = node?.networkElementType,
        sharedCells = node.sharedCells,
        nodeType = node.nodeType,
        partName = node.partName,
        vendorName = node.vendorName,
        radioAccessTechnology = node.radioAccessTechnology,
        nodeCount = node.cellId !== undefined ? nodeNameCounts.get(node.cellId) : 0,
        displayName = node.displayName,
        partCode = node.partCode;
    let element;
    const nodeOptions = [];

    if (nodeCount > 0 || cellId) {
        const commonName = !!node?.commonName ? `${node?.commonName}_` : '';
        const text = commonName.concat(node.nodeName.concat(!!cellId ? `_CELL-${cellId}` : ''));
        const option = node.nodeName.concat(!!cellId ? `_CELL-${cellId}` : '');
        nodeOptions.push({
            key: option,
            text: text,
            value: option,
            commonName: node?.commonName,
            nodeType: nodeType,
            cellType: cellType,
            networkElementType: networkElementType,
            radioAccessTechnology: radioAccessTechnology,
            unitId: unitIdVal,
            vendorName
        });
        cellRadioMap[option] = [...(cellRadioMap[option] || []), node.unitId];
    } else if (sharedCells?.length > 0 && !isOutdoor5GBB) {
        sharedCells.forEach((sc) => {
            const commonName = !!node?.commonName ? `${node?.commonName}_` : '';
            const text = commonName.concat(node.nodeName.concat(!!sc.cellId ? `_CELL-${sc.cellId}` : ''));
            const option = node.nodeName.concat(!!sc.cellId ? `_CELL-${sc.cellId}` : '');
            nodeOptions.push({
                key: option,
                text: text,
                value: option,
                commonName: node?.commonName,
                cellType: sc?.cellType,
                networkElementType: networkElementType,
                radioAccessTechnology: radioAccessTechnology,
                nodeType: nodeType,
                unitId: unitIdVal,
                vendorName
            });
            cellRadioMap[option] = [...(cellRadioMap[option] || []), node.unitId];
        });
    } else {
        let retValTemp = unitIdVal?.split('-');
        if ((nodeCount > 1 || cellType === 'FDD' || sharedCells != null) && retValTemp.length >= 2) {
            element = retValTemp[0].concat('-').concat(retValTemp[1]);
        } else {
            element = unitIdVal;
        }

        const commonName = !!node?.commonName ? `${node?.commonName}_` : '';
        const option = node.nodeName.concat(!!element ? (isNDAC && nodeType === 'CELLS' ? `_CELL-${partName}${element}` : `_${element}`) : '');
        let optionText = commonName.concat(
            node.nodeName.concat(!!element ? (isNDAC && nodeType === 'CELLS' ? `_CELL-${partName}${element}` : `_${element}`) : '')
        );
        if (isNDAC && displayName) {
            optionText = displayName;
            if (nodeType === 'CELLS') {
                optionText += `_${partCode}_CELL_${partName}`;
            }
        }
        const optionValue = !addUnitFlagValueInOptionValue
            ? node.nodeName
            : node.nodeName.concat(!!element ? (isNDAC && nodeType === 'CELLS' ? `_CELL-${partName}${element}` : `_${element}`) : '');
        nodeOptions.push({
            key: option,
            text: optionText,
            value: option,
            commonName: node?.commonName,
            optionvalue: optionValue.replace(/(\r\n|\n|\r)/gm, ''),
            nodeType: nodeType,
            cellType: cellType,
            networkElementType: networkElementType,
            radioAccessTechnology: radioAccessTechnology,
            unitId: unitIdVal,
            vendorName
        });
    }

    return nodeOptions;
};

export const multipleArrayNodes = (
    multiArray = [],
    flag,
    addUnitFlagInOptionValue,
    isManageAllowed5G,
    cellRadioMap = {},
    isNDAC,
    isOutdoor5GBB = false
) => {
    let newArray = [];
    for (let num = 0; num < multiArray.length; num++) {
        const temp = multiArray[num].nodeList;
        let nodeNameCounts = new Map();
        if (temp?.length > 0) {
            for (let nodeCount = 0; nodeCount < temp.length; nodeCount++) {
                //console.log("node Object [" + temp[nodeCount] + "]");
                var key = temp[nodeCount];
                var cellIdForNode = key.cellId;
                if (cellIdForNode != undefined) {
                    var count = nodeNameCounts.get(cellIdForNode);
                    //console.log("Cell ID [" + cellIdForNode + "][" + count + "]");
                    if (count === undefined) {
                        count = 1;
                    } else {
                        count = count + 1;
                    }
                    //console.log("UPDATED NODE [" + cellIdForNode + "][" + count + "]");
                    nodeNameCounts.set(cellIdForNode, count);
                }
            }
            //console.log(nodeNameCounts);
            if (flag === 'partName') {
                temp.forEach((key) => {
                    newArray.push(getPartNameNodeOption(key));
                });
            } else if (flag === 'unitId') {
                temp.forEach((key) => {
                    newArray = newArray.concat(
                        getUnitIdNodeOption(key, nodeNameCounts, cellRadioMap, addUnitFlagInOptionValue, isNDAC, isOutdoor5GBB)
                    );
                });
            } else
                temp.forEach((key) => {
                    const commonName = !!key?.commonName ? `${key?.commonName}_` : '';
                    const text = commonName.concat(key.nodeName);
                    newArray.push({
                        key: key.nodeName,
                        text: text,
                        value: key.nodeName,
                        commonName: key?.commonName,
                        nodeType: key.nodeType,
                        unitId: key.unitId,
                        vendorName: key.vendorName,
                        cellType: key?.cellType,
                        networkElementType: key?.networkElementType,
                        radioAccessTechnology: key.radioAccessTechnology
                    });
                });
        }
    }

    const uniqueArray = Array.from(new Set(newArray.map((a) => a.key))).map((key) => {
        return newArray.find((a) => a.key === key);
    });

    return uniqueArray;
};

export const findTimerValues = (timer, valueFromDate, valueToDate, timezone) => {
    if (valueFromDate._isAMomentObject) {
        valueFromDate = valueFromDate.toDate();
    }
    if (valueToDate._isAMomentObject) {
        valueToDate = valueToDate.toDate();
    }

    const timerChanged = { ...timer };

    const dateDiff = Math.floor((valueToDate - valueFromDate) / (1000 * 60 * 60 * 24));

    const comparePresentValueDate = moment.utc(valueFromDate).tz(timezone).format('YYYY-MM-DD');
    const presentDate = moment.utc(new Date()).tz(timezone).format('YYYY-MM-DD');
    const compareTodayDate = moment.utc(valueToDate).tz(timezone).format('YYYY-MM-DD');

    const getHours = valueToDate.getHours();
    const getMins = valueToDate.getMinutes();
    const getHourdiff = getHours + getMins / 60;
    const daysInHour = getHourdiff;

    if (dateDiff < 2) {
        let hr = 24;
        let step = 15;
        if (daysInHour < 24 && presentDate === comparePresentValueDate) {
            hr = Math.floor(daysInHour);
            step = Math.floor((getHourdiff * 15) / 24);
        } else if (daysInHour < 24 && dateDiff > 0 && presentDate == compareTodayDate) {
            hr = 24 + Math.floor(daysInHour);
            const temp2 = 1 + daysInHour / 24;
            step = Math.floor(temp2 * 15);
        } else if (dateDiff > 0 && presentDate !== compareTodayDate) {
            hr = 48;
            step = 2 * 15;
        }
        timerChanged.hr = hr;
        timerChanged.step = step;
        timerChanged.min = 0;
        timerChanged.sptType = 'minute';
        timerChanged.scale = 1;
    } else if (dateDiff >= 2 && dateDiff < 31) {
        if (presentDate !== compareTodayDate) {
            timerChanged.hr = dateDiff * 24;
            timerChanged.step = (dateDiff + 1) * 15;
        } else {
            timerChanged.hr = (dateDiff - 1) * 24 + Math.floor((getHourdiff / 24) * 15);
            timerChanged.step = dateDiff * 15 + Math.floor((getHourdiff * 15) / 24);
        }
        timerChanged.min = 0;
        timerChanged.sptType = 'minute';
        timerChanged.scale = 2;
    } else if (dateDiff >= 31 && dateDiff < 366) {
        if (presentDate !== compareTodayDate) {
            timerChanged.hr = dateDiff * 24;
            timerChanged.step = (dateDiff + 1) * 15;
        } else {
            timerChanged.hr = (dateDiff - 1) * 24 + Math.floor((getHourdiff / 24) * 15);
            timerChanged.step = dateDiff * 15 + Math.floor((getHourdiff * 15) / 24);
        }
        timerChanged.min = 0;
        timerChanged.sptType = 'minute';
        timerChanged.scale = 3;
    } else if (dateDiff >= 366) {
        if (presentDate !== compareTodayDate) {
            timerChanged.hr = dateDiff * 24;
            timerChanged.step = (dateDiff + 1) * 15;
        } else {
            timerChanged.hr = (dateDiff - 1) * 24 + Math.floor((getHourdiff / 24) * 15);
            timerChanged.step = dateDiff * 15 + Math.floor((getHourdiff * 15) / 24);
        }
        timerChanged.min = 0;
        timerChanged.sptType = 'minute';
        timerChanged.scale = 4;
    }
    return timerChanged;
};

export const decorateOverallKPI = (kpiDataView, kpiDefaultData, kpiType, needValue) => {
    const KPIData = kpiDataView.find((kpi) => kpi.kpiType === kpiType);
    const defaultThresholds = kpiDefaultData.find((thresold) => thresold.kpiType === kpiType)?.thresholds;

    if (!KPIData || !defaultThresholds) return;

    return {
        ...KPIData,
        ...(needValue && { value: Math.round(Number(KPIData.value) * 1000) / 10 }),
        defaultThresholds: { ...defaultThresholds }
    };
};

export const decorateThroughputKPI = (kpiDataView, kpiDefaultData = []) => {
    if (kpiDataView.length == 0) return;

    const throughtputData = kpiDataView.filter((kpi) => kpi.kpiType === 'DOWNLINK_THROUGHPUT' || kpi.kpiType === 'UPLINK_THROUGHPUT');

    const {
        value: dlMsg1,
        kpiType: dlKpiType,
        thresholds: ctpThresholds
    } = throughtputData?.find((throughtputKPI) => throughtputKPI.kpiType === 'DOWNLINK_THROUGHPUT') ?? {};
    const { maxVal: max } = getInfo(dlKpiType);

    const { value: ulMsg1 } = throughtputData?.find((throughtputKPI) => throughtputKPI.kpiType === 'UPLINK_THROUGHPUT') ?? {};
    const totalMsg1 = Math.round((parseFloat(dlMsg1) + parseFloat(ulMsg1)) * 100) / 100;
    const ctpRate = Math.round(((totalMsg1 * 100) / max) * 10) / 10;
    const ctpDefaultThresholds = kpiDefaultData?.find((thresold) => thresold.kpiType === dlKpiType)?.thresholds;

    return throughtputData.length > 0
        ? {
              value: ctpRate,
              kpiType: dlKpiType,
              thresholds: ctpThresholds,
              defaultThresholds: { ...ctpDefaultThresholds }
          }
        : null;
};

export const getAlertList = (kpiDataView) => {
    if (!kpiDataView) return [0, 0, 0];

    let [critical, major, optimal] = [0, 0, 0];
    const attachedDevicesData = kpiDataView?.find((kpi) => kpi.kpiType === 'ATTACHED_DEVICES');
    const attachDeviceRate = ((attachedDevicesData?.value ?? 0) * 100) / getInfo('ATTACHED_DEVICES').maxVal;

    const attachRate = kpiDataView?.find((kpi) => kpi.kpiType === 'ATTACH_SUCCESS');
    const attachRateKPI = attachRate && {
        value: Math.round(Number(attachRate.value) * 1000) / 10,
        thresholds: {
            ...attachRate.thresholds
        }
    };

    //combinedThroughput KPI:
    const throughputKPI = decorateThroughputKPI(kpiDataView);

    const getCount = (val = 0, thresholds) => {
        if (val <= thresholds?.level1) {
            optimal += 1;
        } else if (val > thresholds?.level2 && val < thresholds?.level3) {
            major += 1;
        } else {
            critical += 1;
        }
    };
    attachedDevicesData && getCount(attachDeviceRate, attachedDevicesData?.thresholds);
    attachRateKPI && getCount(attachRateKPI?.value, attachRateKPI?.thresholds);
    throughputKPI && getCount(throughputKPI?.value, throughputKPI?.thresholds);

    return [critical, major, optimal];
};

export const radioTooltipMapping = {
    Accessibility: 'Shows the ability of customer to access the network',
    Mobility: 'Shows the ability of customer to continue service while moving within the network',
    Retainability: 'Shows the ability of customer to retain service to the network',
    Throughput: 'Shows uplink and downlink throughput of the RAN network',
    // 'Data retainability (dropped session rate)': 'Shows the ability of customer to retain service to the network',
    // 'Data accessibility (blocked session rate)': 'Shows the ability of customer to access the network',
    '5G NSA accessibility': 'Shows the ability of customer to access the network',
    '5G NSA retainability': 'Shows the ability of customer to retain service to the network',
    '4G RAN data accessibility': 'This shows the 4G blocked data session rate',
    '4G RAN data retainability': 'This shows the 4G dropped data session rate',
    '4G RAN data mobility': 'This shows the 4G data mobility success rate',
    'VoLTE accessibility': 'This shows the 4G blocked voice session rate',
    'VoLTE retainability': 'This shows the 4G dropped voice session rate',
    'VoLTE mobility': 'This shows the 4G voice mobility success rate',
    '5G NSA RAN data accessibility': 'This shows the 5G NSA blocked data session rate',
    '5G NSA RAN data retainability': 'This shows the 5G NSA dropped data session rate',
    '5G NSA RAN data mobility': 'This shows the 5G NSA data mobility success rate',
    '4G RAN throughput': 'This shows the 4G downlink and uplink throughput of the RAN network',
    '5G RAN throughput': 'This shows the 5G downlink and uplink throughput of the RAN network',
    '4G5G RAN throughput': 'This shows the 4G5G downlink and uplink throughput of the RAN network'
};

export const prepareOverallWidgetContent = (title, deviceCount, thresholds, isEP5G, isIBC) => {
    switch (title) {
        case 'Accessibility':
        case '4G RAN Accessibility':
            return {
                tooltip: radioTooltipMapping[title],
                COLORS: ['#00AC3E', '#FFBC3D', '#ed7000'],
                type: 'Accessibility',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: title === '4G RAN Accessibility' ? '13px' : '-30px'
            };
        case 'Mobility':
        case '4G RAN Mobility':
            return {
                tooltip: radioTooltipMapping[title],
                COLORS: ['#ed7000', '#FFBC3D', '#00AC3E'],
                type: 'Mobility',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: title === '4G RAN Accessibility' ? '-11px' : '-54px'
            };
        case 'Retainability':
        case '4G RAN Retainability':
            return {
                tooltip: radioTooltipMapping[title],
                COLORS: ['#00AC3E', '#FFBC3D', '#ed7000'],
                type: 'Retainability',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: title === '4G RAN Accessibility' ? '11px' : '-32px'
            };
        case 'Attached devices':
            return {
                tooltip: 'Shows the total number of devices connected to the network',
                COLORS: ['#00AC3E', '#4A1C02'],
                type: 'Attached devices',
                data: [
                    { name: 'Online', value: deviceCount?.enabledCount },
                    { name: 'Offline', value: deviceCount?.offlineCount }
                ],
                leftValue: '-7px'
            };
        case 'Attach success rate':
            return {
                tooltip: 'Shows the successful attach percentage out of total attempts',
                COLORS: ['#ed7000', '#FFBC3D', '#00AC3E'],
                type: 'Attach success rate',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: '5px'
            };
        case 'Attached success ratio':
            return {
                tooltip: 'Shows total number of devices success, connected and failures',
                COLORS: ['#00AC3E', '#ED7000'],
                type: 'Attached success ratio',
                data: [
                    { name: 'Success', value: deviceCount?.successCount },
                    { name: 'Failures', value: deviceCount?.failuresCount }
                ],
                leftValue: '8px'
            };
        case 'Uplink throughput':
            return {
                tooltip: 'Shows uplink throughput of the ' + (isEP5G ? 'RAN' : isIBC ? 'radio' : 'core') + ' network',
                COLORS: [],
                type: 'Uplink throughput',
                data: [],
                leftValue: '-4px'
            };
        case 'Downlink throughput':
            return {
                tooltip: 'Shows downlink throughput of the ' + (isEP5G ? 'RAN' : isIBC ? 'radio' : 'core') + ' network',
                COLORS: [],
                type: 'Downlink throughput',
                data: [],
                leftValue: '11px'
            };

        case 'Core throughput':
            return {
                tooltip: 'Shows uplink and downlink throughput of the Core',
                COLORS: [],
                type: 'Core throughput',
                data: [],
                leftValue: '-10px'
            };
        case '4G RAN throughput':
            return {
                tooltip: 'Shows uplink and downlink throughput of the 4G RAN',
                COLORS: [],
                type: '4G RAN throughput',
                data: [],
                leftValue: '7px'
            };
        case '5G NSA RAN throughput':
            return {
                tooltip: 'Shows uplink and downlink throughput of the 5G NSA RAN',
                COLORS: [],
                type: '5G NSA RAN throughput',
                data: [],
                leftValue: '31px'
            };
        case 'VoLTE accessibility':
            return {
                tooltip: radioTooltipMapping[title],
                COLORS: ['#00AC3E', '#FFBC3D', '#ed7000'],
                type: 'VoLTE accessibility',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: '43px'
            };
        case 'VoLTE retainability':
            return {
                tooltip: radioTooltipMapping[title], //TODO
                COLORS: ['#00AC3E', '#FFBC3D', '#ed7000'],
                type: 'VoLTE retainability',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: '39px'
            };
        case 'VoLTE mobility':
            return {
                tooltip: radioTooltipMapping[title],
                COLORS: ['#ed7000', '#FFBC3D', '#00AC3E'],
                type: 'Voice Mobility',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: '18px'
            };
        case '5G NSA accessibility':
            return {
                tooltip: radioTooltipMapping[title],
                COLORS: ['#00AC3E', '#FFBC3D', '#ed7000'],
                type: '5G NSA accessibility',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: '13px'
            };
        case '5G NSA retainability':
            return {
                tooltip: radioTooltipMapping[title],
                COLORS: ['#00AC3E', '#FFBC3D', '#ed7000'],
                type: '5G NSA retainability',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: '11px'
            };
        case '5G NSA mobility':
            return {
                tooltip: radioTooltipMapping[title],
                COLORS: ['#00AC3E', '#FFBC3D', '#ed7000'],
                type: '5G NSA mobility',
                data: [
                    { name: 'level1', value: thresholds?.level1 },
                    { name: 'level2', value: thresholds?.level2 },
                    { name: 'level3', value: thresholds?.level3 }
                ],
                leftValue: '11px'
            };
        default:
            return {
                tooltip: 'Shows uplink and downlink throughput of the ' + (isEP5G ? 'RAN' : 'core') + ' network',
                COLORS: [],
                type: 'default',
                data: [],
                leftValue: isEP5G ? '40px' : '42px'
            };
    }
};

export const processGaugeChartData = (data, KPIValue, COLORS) => {
    //use threshold to divide the whole range into 3 parts and add KPI label as needed
    const [{ value: threshold1 }, threshold, { value: threshold2 }] = data,
        tickLabels = [
            { num: 0, color: COLORS[0] },
            { num: threshold1, color: COLORS[0] },
            { num: threshold2, color: COLORS[1] },
            { num: 100, color: COLORS[2] }
        ];

    if (0 < KPIValue && KPIValue < threshold1) {
        tickLabels.splice(1, 0, { num: KPIValue, color: COLORS[0] });
    } else if (threshold1 < KPIValue && KPIValue < threshold2) {
        tickLabels.splice(2, 0, { num: KPIValue, color: COLORS[1] });
    } else if (threshold2 < KPIValue && KPIValue < 100) {
        tickLabels.splice(3, 0, { num: KPIValue, color: COLORS[2] });
    }

    return { tickLabels, dataValue: KPIValue };
};

export const processData = (data) => {
    const result = [];
    const valArray = data?.arrVal?.map((d) => Math.ceil(d.value) || 1);
    const maxVal = Math.max(...(valArray ?? [])) < 0 ? 1 : Math.max(...(valArray ?? []));
    const values = data?.arrVal.map((item) => {
        if (item.value === undefined || !item?.value) {
            item.value = 0.0;
        } else {
            const numValue = Number(item.value);
            if (numValue <= -1.0) {
                item.NoData = maxVal;
                item.value = undefined;
            } else {
                item.value = numValue;
            }
        }
        return item;
    });

    const sortedData = (values ?? []).sort(function (a, b) {
        return a.time - b.time;
    });

    for (let num = 0; num < sortedData.length; num++) {
        result.push({
            value: sortedData[num].NoData !== undefined ? undefined : sortedData[num].value,
            NoData: sortedData[num].NoData,
            rawTime: sortedData[num].time
        });
    }

    return result;
};

/**
 * Fetches the ATTACHED_DEVICES value from the overallKPIS array in the given JSON object.
 * Returns the value as a number. If not found or invalid, returns 0.
 * @param {Object} data - The JSON object containing overallKPIS.
 * @returns {number}
 */
export function getAttachedDevicesValue(overallKPIS) {
    if (!overallKPIS || !Array.isArray(overallKPIS)) return 0;
    const kpi = overallKPIS.find((item) => item.kpiType === 'ATTACHED_DEVICES');
    if (!kpi || typeof kpi.value === 'undefined') return 0;
    const num = Number(kpi.value);
    return isNaN(num) ? 0 : num;
}
